import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import styles from './CarouselSlider.module.scss';
import { IMAGE_MIME_TYPES } from '../../components/Uploader';
import CarouselDropzone from './CarouselDropzone';
import CarouselImage from './CarouselImage';
import type { ImageItem } from './Carousel';
import { useCarousel } from './Carousel';
import { DROPZONE_WIDTH } from './ImageNode';

const CUT_OFF_THRESHOLD = 0.25;
const GAP = 24;
const TRANSITION = {
  type: 'spring',
  damping: 30,
  stiffness: 300,
  mass: 0.2,
} as const;

export type Dimensions = {
  width: number;
  height: number;
};

type Position = {
  left: number;
  width: number;
  contain: boolean;
};

type Positions = Record<string, Position>;

function getAdjustedWidth(dimensions: Dimensions, containerHeight: number) {
  const { width: originalWidth, height: originalHeight } = dimensions;
  const ratio = originalHeight > 0 ? originalWidth / originalHeight : 0;
  return containerHeight * ratio;
}

function getAdjustedHeight(dimensions: Dimensions, containerWidth: number) {
  const { width: originalWidth, height: originalHeight } = dimensions;
  const ratio = originalHeight > 0 ? originalWidth / originalHeight : 0;
  return containerWidth / ratio;
}

function getSummedWidth({
  items,
  containerDimensions,
  gap,
  dimensionsMap,
}: {
  items: ImageItem[];
  containerDimensions: Dimensions;
  gap: number;
  dimensionsMap: Map<string, Dimensions>;
}) {
  return items.reduce((acc, item) => {
    const dimensions = dimensionsMap.get(item.image.id ?? item.image.source);
    if (!dimensions) return acc;

    const adjustedWidth = getAdjustedWidth(
      dimensions,
      containerDimensions.height,
    );
    const width = Math.min(containerDimensions.width, adjustedWidth);
    return acc + width + gap;
  }, 0);
}

function findIndexByPosition(
  items: ImageItem[],
  positions: Positions,
  xPosition: number,
) {
  return items.findIndex((item, i) => {
    const currentPosition = positions[item.id];
    const next = items[i + 1];
    const nextPosition = next && positions[next.id];

    if (currentPosition != null && nextPosition == null) {
      return xPosition >= currentPosition.left + currentPosition.width / 2;
    }

    if (currentPosition == null || nextPosition == null) {
      return false;
    }

    const prevBound = currentPosition.left + currentPosition.width / 2;
    const nextBound = nextPosition.left + nextPosition.width / 2;
    return xPosition >= prevBound && xPosition < nextBound;
  });
}

export function calculatePositions({
  items,
  containerDimensions,
  singleMode,
  gap = 0,
  dimensionsMap,
}: {
  items: ImageItem[];
  containerDimensions: Dimensions;
  singleMode?: boolean;
  gap?: number;
  dimensionsMap: Map<string, Dimensions>;
}): Positions {
  const selectedIndex = Math.floor(items.length / 2);
  const widthBefore = getSummedWidth({
    items: items.slice(0, selectedIndex),
    containerDimensions,
    gap,
    dimensionsMap,
  });

  const selectedItemWidth = getSummedWidth({
    items: items.slice(selectedIndex, selectedIndex + 1),
    containerDimensions,
    gap,
    dimensionsMap,
  });

  let startLeft =
    -(widthBefore + selectedItemWidth / 2 - containerDimensions.width / 2) +
    gap / 2;

  return items.reduce((acc, item, i) => {
    const dimensions = dimensionsMap.get(item.image.id ?? item.image.source);
    if (!dimensions) return acc;

    const position = (() => {
      const containerWidth = containerDimensions.width;
      const adjustedWidth = getAdjustedWidth(
        dimensions,
        containerDimensions.height,
      );
      const cutOff = adjustedWidth - containerWidth;
      const width = Math.min(containerWidth, adjustedWidth);

      if (singleMode) {
        if (i === selectedIndex) {
          return {
            left: 0,
            width,
            contain: true,
          };
        }

        return {
          left:
            i < selectedIndex ? -(containerWidth + gap) : containerWidth + gap,
          width,
          contain: true,
        };
      }

      const currentLeft = startLeft;

      startLeft += width + gap;
      return {
        left: currentLeft,
        width,
        contain: cutOff / adjustedWidth >= CUT_OFF_THRESHOLD,
      };
    })();

    return {
      ...acc,
      [item.id]: position,
    };
  }, {} as Positions);
}

function useCarouselDropzone({
  singleMode,
  gap,
  positions,
  containerRef,
}: {
  singleMode: boolean;
  gap: number;
  positions: Positions;
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const { items } = useCarousel();

  const [xPosition, setXPosition] = useState<number | null>(null);

  const dropzone = useMemo(() => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (xPosition == null || containerRect == null) return null;
    const width = Math.min(DROPZONE_WIDTH, containerRect.width);
    const index = findIndexByPosition(items, positions, xPosition);

    if (index === -1) {
      const firstItem = items[0];
      if (!firstItem) return null;
      const firstItemPosition = positions[firstItem.id];
      if (!firstItemPosition) return null;

      return {
        index: -1,
        offset: 0,
        width,
        left: firstItemPosition.left,
      };
    }
    const itemBeforeDropzone = items[index];
    if (!itemBeforeDropzone) return null;

    const previousPosition = positions[itemBeforeDropzone.id];
    if (!previousPosition) return null;

    let offset = width / 2;
    const nextLeft = previousPosition.left + previousPosition.width + gap;

    if (nextLeft - offset <= 0) {
      offset = Math.max(0, nextLeft);
    } else if (nextLeft + width > containerRect.width) {
      offset = nextLeft + width - containerRect.width;
    }

    let left = nextLeft - offset;

    if (singleMode) {
      left = previousPosition.left < 0 ? 0 : containerRect.width - width;
    }

    return {
      index,
      offset,
      width,
      left,
    };
  }, [xPosition, containerRef, items, positions, singleMode, gap]);

  return {
    dropzone,
    onDragOver(clientX: number) {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        setXPosition(clientX - containerRect.x);
      }
    },
    onDragEnd: () => setXPosition(null),
  };
}

function usePositions({
  containerRef,
  singleMode,
  gap,
  maxHeight,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  singleMode: boolean;
  gap: number;
  maxHeight?: number;
}) {
  const { items } = useCarousel();
  const [containerRect, setContainerRect] = useState<DOMRect | null>(
    () => containerRef.current?.getBoundingClientRect() ?? null,
  );
  const containerWidth = containerRect?.width ?? 0;
  const dimensionsMap = useRef<Map<string, Dimensions>>(new Map());

  const maxImageHeight = items.reduce((acc, item) => {
    const dimensions = dimensionsMap.current.get(
      item.image.id ?? item.image.source,
    );
    if (!dimensions) return acc;
    return Math.max(acc, getAdjustedHeight(dimensions, containerWidth));
  }, 0);

  const positions = containerRect
    ? calculatePositions({
        items,
        containerDimensions: containerRect,
        singleMode,
        gap,
        dimensionsMap: dimensionsMap.current,
      })
    : {};

  const [, rerender] = useReducer(() => ({}), {});

  useEffect(() => {
    const containerEl = containerRef.current;
    if (containerEl == null) return undefined;

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) {
        // We can't prevent "ResizeObserver loop limit exceeded" errors since
        // we need to recalculate the container height when the carousel is resized.
        // Setting the container rect triggers rerender, and that will lead to
        // ResizeObserver events that cannot be delivered anymore.
        setContainerRect(entry.contentRect);
      }
    });

    resizeObserver.observe(containerEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  const initialRenderRef = useRef(true);

  useLayoutEffect(() => {
    if (initialRenderRef) {
      initialRenderRef.current = false;
    }
  }, []);

  const containerHeight = (() => {
    if (initialRenderRef.current) {
      return maxHeight ?? 'auto';
    }

    return maxHeight != null
      ? Math.min(maxHeight, maxImageHeight)
      : maxImageHeight;
  })();
  return {
    onMeasure: (image: ImageItem['image'], dimensions: Dimensions) => {
      dimensionsMap.current.set(image.id ?? image.source, dimensions);
      const isFullyMeasured = items
        .map((item) =>
          dimensionsMap.current.get(item.image.id ?? item.image.source),
        )
        .every((d) => d != null);

      if (isFullyMeasured) {
        rerender();
      }
    },
    positions,
    containerWidth,
    containerHeight,
  };
}

interface CarouselSliderProps {
  singleMode?: boolean;
  dragDisabled?: boolean;
  maxHeight?: number;
  fullscreen?: boolean;
}

const CarouselSlider = ({
  singleMode = false,
  dragDisabled,
  maxHeight,
  fullscreen,
}: CarouselSliderProps) => {
  const { items, addImages } = useCarousel();
  const containerRef = useRef<HTMLDivElement>(null);

  const { positions, onMeasure, containerWidth, containerHeight } =
    usePositions({
      containerRef,
      singleMode,
      gap: GAP,
      maxHeight,
    });

  const { onDragEnd, onDragOver, dropzone } = useCarouselDropzone({
    singleMode,
    gap: GAP,
    positions,
    containerRef,
  });

  const { getRootProps } = useDropzone({
    accept: IMAGE_MIME_TYPES,
    disabled: dragDisabled,
    onDragOver: (e) => {
      const hasNonImageItem = Array.from(e.dataTransfer.items).some(
        (item) => !item.type.match(IMAGE_MIME_TYPES),
      );
      if (!hasNonImageItem) {
        onDragOver(e.clientX);
      }
    },
    onDragLeave: onDragEnd,
    onDrop: (files) => {
      if (dropzone) {
        addImages(files, dropzone.index);
        onDragEnd();
      }
    },
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div
      className={styles.container}
      ref={containerRef}
      style={{
        height: containerHeight,
      }}
    >
      {dropzone && (
        <motion.div
          className={classNames(styles.item, styles.dropzone)}
          initial={false}
          animate={{
            left: dropzone.left,
            width: dropzone.width,
          }}
          transition={TRANSITION}
        >
          <CarouselDropzone width={dropzone.width} />
        </motion.div>
      )}

      {items.map((item, i) => {
        const { id } = item;
        const position = positions[id];
        const selectedIndex = Math.floor(items.length / 2);

        const children = (
          <CarouselImage
            src={item.image.source}
            alt={item.image.caption ?? 'User-uploaded image'}
            uploading={item.image.uploadPayload != null}
            contain={position?.contain}
            onMeasure={(dimensions) => onMeasure(item.image, dimensions)}
            fullscreen={fullscreen}
          />
        );

        if (!position) {
          return (
            <div
              key={id}
              className={classNames(styles.item, styles.image)}
              style={{ left: 0, width: 0 }}
            >
              {children}
            </div>
          );
        }

        const left = (() => {
          const centerAdjustedPosition =
            position.left + (containerWidth - position.width) / 2;

          if (singleMode) {
            if (dropzone && i === selectedIndex) {
              if (i <= dropzone.index) {
                return centerAdjustedPosition - dropzone.width - GAP;
              }
              return centerAdjustedPosition + dropzone.width + GAP;
            }

            return centerAdjustedPosition;
          }

          if (dropzone) {
            if (i <= dropzone.index) {
              return position.left - dropzone.offset;
            }
            return position.left + dropzone.width - dropzone.offset + GAP;
          }

          return position.left;
        })();

        const initial = (() => {
          if (i > selectedIndex) {
            return {
              left: 3 * containerWidth,
              width: containerWidth,
            };
          }
          if (i < selectedIndex) {
            return {
              left: -3 * containerWidth,
              width: containerWidth,
            };
          }
          return false;
        })();

        return (
          <motion.div
            key={id}
            className={classNames(styles.item, styles.image)}
            initial={initial}
            animate={{
              left,
              width: position.width,
            }}
            transition={TRANSITION}
            data-id={id}
          >
            {children}
          </motion.div>
        );
      })}

      {!dragDisabled && (
        <div className={styles.dragInterceptor} {...getRootProps()} />
      )}
    </div>
  );
};

export default CarouselSlider;
